import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { DollarSign, Download, FileText, Loader2, Send, Trash2, XCircle } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useToast } from '../hooks/useToast';
import { Invoice, InvoiceStatus, PAYMENT_METHOD_LABELS, PaymentMethod, STATUS_COLORS, STATUS_LABELS } from '../types/invoice';
import { DeleteConfirmModal } from '../components/DeleteConfirmModal';
import { RecordPaymentModal } from '../components/RecordPaymentModal';

export function InvoiceDetailPage() {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const { get, post, del, getBlob } = useApi();

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPayment, setShowPayment] = useState(false);
  const [showVoid, setShowVoid] = useState(false);
  const toast = useToast();

  const fetchInvoice = useCallback(async () => {
    if (!invoiceId) return;
    setLoading(true);
    try {
      const result = await get<Invoice>(`/invoices/${invoiceId}`);
      if (result.success && result.data) setInvoice(result.data);
      else setError(result.error || 'Failed to load');
    } catch { setError('Failed to load invoice'); }
    finally { setLoading(false); }
  }, [invoiceId, get]);

  useEffect(() => { fetchInvoice(); }, [fetchInvoice]);

  const handleSend = useCallback(async () => {
    if (!invoiceId) return;
    try {
      const result = await post(`/invoices/${invoiceId}/send`);
      if (result.success) fetchInvoice();
      else setError(result.error || 'Failed to send');
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.data?.error) setError(err.response.data.error);
      else setError('Failed to send invoice');
    }
  }, [invoiceId, post, fetchInvoice]);

  const handleVoid = useCallback(async () => {
    if (!invoiceId) return;
    try {
      const result = await del(`/invoices/${invoiceId}`, { data: { reason: 'Voided by staff' } });
      if (result.success) {
        setShowVoid(false);
        fetchInvoice();
      } else setError(result.error || 'Failed to void');
    } catch { setError('Failed to void invoice'); }
  }, [invoiceId, del, fetchInvoice]);

  const handlePaymentRecorded = useCallback(() => {
    setShowPayment(false);
    fetchInvoice();
  }, [fetchInvoice]);

  const handleSendBalance = useCallback(async () => {
    if (!invoiceId) return;
    try {
      const result = await post(`/invoices/${invoiceId}/send-balance`);
      if (result.success) {
        toast.show('Balance statement marked as sent.', 'success');
      } else {
        setError(result.error || 'Failed to send balance statement');
      }
    } catch { setError('Failed to send balance statement'); }
  }, [invoiceId, post]);

  const handleDownloadPdf = useCallback(async () => {
    if (!invoiceId) return;
    setDownloadingPdf(true);
    setError('');
    try {
      const blob = await getBlob(`/invoices/${invoiceId}/pdf`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `INV-${invoice?.invoiceNumber || invoiceId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { setError('Failed to download PDF'); }
    finally { setDownloadingPdf(false); }
  }, [invoiceId, invoice?.invoiceNumber]);

  const [showWriteOff, setShowWriteOff] = useState(false);
  const [writeOffReason, setWriteOffReason] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const handleWriteOff = useCallback(async () => {
    if (!invoiceId || !writeOffReason) return;
    try {
      const result = await post(`/invoices/${invoiceId}/write-off`, { reason: writeOffReason });
      if (result.success) {
        setShowWriteOff(false);
        setWriteOffReason('');
        toast.show('Invoice balance written off.', 'success');
        fetchInvoice();
      } else setError(result.error || 'Failed to write off');
    } catch { setError('Failed to write off invoice'); }
  }, [invoiceId, writeOffReason, post, fetchInvoice]);

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;
  }
  if (!invoice) {
    return <div className="flex items-center justify-center py-20 text-red-600">{error || 'Invoice not found'}</div>;
  }

  const isDraft = invoice.status === 'draft';
  const isVoided = invoice.status === 'voided';
  const canPay = !isDraft && !isVoided && invoice.balance > 0;

  return (
    <>
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900">{invoice.invoiceNumber}</h1>
              <p className="text-sm text-gray-500">
                <button onClick={() => navigate(`/patients/${invoice.patientId}`)} className="text-blue-600 hover:underline">
                  {invoice.patientName}
                </button>
                {' · '}{invoice.createdAt?.slice(0, 10)}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${STATUS_COLORS[invoice.status as InvoiceStatus]}`}>
                {STATUS_LABELS[invoice.status as InvoiceStatus]}
              </span>
              <button onClick={handleDownloadPdf} disabled={downloadingPdf}
                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1.5">
                {downloadingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} PDF
              </button>
              {!isVoided && invoice.status !== 'written_off' && (
                <button onClick={handleSend}
                  className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center gap-1.5">
                  <Send className="h-4 w-4" /> {isDraft ? 'Send' : 'Resend'}
                </button>
              )}
              {canPay && (
                <>
                  <button onClick={handleSendBalance}
                    className="px-3 py-2 text-sm font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-md hover:bg-orange-100 flex items-center gap-1.5">
                    <FileText className="h-4 w-4" /> Send Balance Statement
                  </button>
                  <button onClick={() => setShowPayment(true)}
                    className="px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 flex items-center gap-1.5">
                    <DollarSign className="h-4 w-4" /> Record Payment
                  </button>
                </>
              )}
              {canPay && invoice.status !== 'written_off' && (
                <button onClick={() => setShowWriteOff(true)}
                  className="px-3 py-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md hover:bg-amber-100 flex items-center gap-1.5">
                  <XCircle className="h-4 w-4" /> Write Off
                </button>
              )}
              {!isVoided && invoice.status !== 'written_off' && (
                <button onClick={() => setShowVoid(true)}
                  className="px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 flex items-center gap-1.5">
                  <Trash2 className="h-4 w-4" /> Void
                </button>
              )}
            </div>
          </div>
          {error && <div className="mt-3 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}
          {toast.message && <div className="mt-3 p-3 rounded bg-green-50 border border-green-200 text-green-700 text-sm">{toast.message}</div>}
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <p className="text-xs text-gray-500">Subtotal</p>
            <p className="text-xl font-bold text-gray-900">R {(invoice.subtotal ?? 0).toFixed(2)}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <p className="text-xs text-gray-500">Paid</p>
            <p className="text-xl font-bold text-green-700">R {(invoice.totalPaid ?? 0).toFixed(2)}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <p className="text-xs text-gray-500">Balance</p>
            <p className={`text-xl font-bold ${(invoice.balance ?? 0) > 0 ? 'text-red-600' : 'text-green-700'}`}>
              R {(invoice.balance ?? 0).toFixed(2)}
            </p>
          </div>
        </div>

        {/* Void reason */}
        {invoice.voidReason && (
          <div className="p-4 rounded bg-red-50 border border-red-200">
            <p className="text-sm font-medium text-red-800">Voided: {invoice.voidReason}</p>
            <p className="text-xs text-red-600 mt-1">{invoice.voidedAt?.slice(0, 10)}</p>
          </div>
        )}

        {/* Line items */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {invoice.lineItems.map((item, i) => (
                <tr key={i}>
                  <td className="px-6 py-3 text-sm font-bold text-gray-900 tracking-wide">{item.code}</td>
                  <td className="px-6 py-3 text-sm text-gray-700">{item.description}</td>
                  <td className="px-6 py-3 text-xs text-gray-500 uppercase">{item.codeType}</td>
                  <td className="px-6 py-3 text-sm text-gray-900 text-right">{item.quantity}</td>
                  <td className="px-6 py-3 text-sm text-gray-900 text-right">R {(item.unitPrice ?? 0).toFixed(2)}</td>
                  <td className="px-6 py-3 text-sm font-medium text-gray-900 text-right">R {(item.total ?? (item.unitPrice ?? 0) * (item.quantity ?? 1)).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Notes */}
        {invoice.notes && (
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-1">Notes</h3>
            <p className="text-sm text-gray-700">{invoice.notes}</p>
          </div>
        )}

        {/* Payment history */}
        {invoice.payments && invoice.payments.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="px-6 py-3 bg-gray-50 border-b">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Payments</h3>
            </div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                  <th className="px-6 py-2 text-left text-xs font-medium text-gray-500">Method</th>
                  <th className="px-6 py-2 text-left text-xs font-medium text-gray-500">Reference</th>
                  <th className="px-6 py-2 text-right text-xs font-medium text-gray-500">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {invoice.payments.map(p => (
                  <tr key={p.id}>
                    <td className="px-6 py-2 text-sm text-gray-700">{p.createdAt?.slice(0, 10)}</td>
                    <td className="px-6 py-2 text-sm text-gray-700">{PAYMENT_METHOD_LABELS[p.method as PaymentMethod] || p.method}</td>
                    <td className="px-6 py-2 text-sm text-gray-500">{p.reference || '—'}</td>
                    <td className="px-6 py-2 text-sm font-medium text-green-700 text-right">R {(p.amount ?? 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {showPayment && invoiceId && (
        <RecordPaymentModal
          invoiceId={invoiceId}
          balance={invoice.balance}
          onClose={() => setShowPayment(false)}
          onRecorded={handlePaymentRecorded}
        />
      )}

      {showVoid && (
        <DeleteConfirmModal
          title="Void Invoice"
          description={`This will void invoice ${invoice.invoiceNumber} (R ${(invoice.subtotal ?? 0).toFixed(2)}). This action cannot be undone.`}
          onConfirm={handleVoid}
          onCancel={() => setShowVoid(false)}
        />
      )}

      {showWriteOff && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h2 className="text-lg font-bold text-gray-900 mb-2">Write Off Balance</h2>
            <p className="text-sm text-gray-600 mb-4">
              Write off the outstanding balance of <strong>R {(invoice.balance ?? 0).toFixed(2)}</strong> on invoice {invoice.invoiceNumber}.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
            <input
              type="text"
              value={writeOffReason}
              onChange={e => setWriteOffReason(e.target.value)}
              placeholder="e.g. Patient deceased, uncollectable debt"
              className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4"
              autoFocus
            />
            <div className="flex justify-end gap-3">
              <button onClick={() => { setShowWriteOff(false); setWriteOffReason(''); }}
                className="px-4 py-2 text-sm text-gray-600">Cancel</button>
              <button onClick={handleWriteOff} disabled={!writeOffReason}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-50">
                Write Off
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
